import { useTenant } from 'application/Shared/Contexts/TenantContext';
import merge from 'deepmerge';
import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react';
import { useSearchParams } from 'react-router';

import { resolveTimeProps } from './Helpers/time';
import { useInterval } from './Helpers/useInterval';
import {
    BasePanel,
    type Context,
    type Dashboard as DashboardType,
    type GroupPanel as GroupPanelType,
    type LayoutItem,
    type Time,
    type Variables
} from './types';
import { useReactQuery } from './useReactQuery';

export interface DashboardState {
    /** Whether the dashboard is filterable. */
    isFiltrable: boolean;
    /** Whether the dashboard is searchable. */
    isSearchable: boolean;
    /** Whether the dashboard is in editing mode. */
    isEditing: boolean;
    /** Whether the dashboard should automatically adjust its layout. */
    autoAdjust: boolean;
    /** Refresh rate or null if disabled. */
    refreshDashboard: number | null;
    /** The last time the dashboard was updated. Timestamp in milliseconds or null if never. */
    dataUpdatedAt: number | null;
    /** Whether the about modal is open. */
    showAbout: boolean;
}

interface BaseDashboardContextValue {
    /** Handles the layout change event. */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    handleLayoutChange: (layout: any[]) => void;
    /** Sets the schema for the dashboard, or group panel. */
    setSchema: (schema: DashboardType | GroupPanelType) => void;
}

export interface DashboardContextValue extends BaseDashboardContextValue {
    /** The state of the dashboard. */
    dashboardState: DashboardState;
    /** The schema of the dashboard. */
    schema: DashboardType | GroupPanelType;
    /** The context of the dashboard. Used to interpolate the dashboard title and description , and passed to panels as context. */
    context: Context;
    /** Pauses the refresh. */
    setRefreshDashboard: React.Dispatch<React.SetStateAction<number | null>>;
    /** Sets the last time the dashboard was updated. */
    setDataUpdatedAt: React.Dispatch<React.SetStateAction<number | null>>;
    /** Sets whether the dashboard should automatically adjust its layout. */
    setAutoAdjust: React.Dispatch<React.SetStateAction<boolean>>;
    /** Sets whether the about modal is open. */
    setShowAbout: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * Context for the Dashboard component.
 */
const DashboardContext = createContext<DashboardContextValue | null>(null);

/**
 * Hook to access the DashboardContext.
 * Throws an error if used outside the DashboardContextProvider.
 */
export const useDashboardContext = () => {
    const context = useContext(DashboardContext);

    if (context === null) {
        throw new Error(
            '`useDashboardContext` must be used within `DashboardContext`'
        );
    }

    return context;
};

interface DashboardContextProviderProps {
    children: React.ReactNode;
    dashboard: DashboardType;
    handleLayoutChange?: (layout: LayoutItem[]) => void;
    setSchema?: (schema: DashboardType | GroupPanelType) => void;
    isEditing?: boolean;
}

/**
 * Provider component for the DashboardContext.
 * Supplies dashboard context and utilities to its children.
 */
export const DashboardContextProvider = ({
    children,
    dashboard,
    handleLayoutChange = () => {},
    setSchema = () => {},
    isEditing = false
}: DashboardContextProviderProps) => {
    const { context: dashboardContext = {} } = dashboard;
    const isFiltrable = Boolean(dashboard.meta?.filters);
    const isSearchable = Boolean(dashboard.meta?.search);

    const [searchParams, setSearchParams] = useSearchParams();
    // If the dashboard has a refresh rate and a time range, use the refresh rate. Null means refresh is disabled.
    const [refreshDashboard, setRefreshDashboard] = useState<number | null>(
        dashboard.meta?.refresh && dashboard.context?.time?.from
            ? dashboard.meta.refresh
            : null
    );
    // The last time the dashboard was updated. Timestamp in milliseconds or null if never.
    const [dataUpdatedAt, setDataUpdatedAt] = useState<number | null>(null);
    // Whether the dashboard should automatically adjust its layout.
    const [autoAdjust, setAutoAdjust] = useState<boolean>(
        (dashboard as DashboardType).meta?.autoAdjust ?? true
    );

    const [showAbout, setShowAbout] = useState<boolean>(false);

    const urlContext = useMemo<Context>(() => {
        const { from, to, ...variables } = Object.fromEntries(
            searchParams as URLSearchParams
        );
        return {
            time: {
                ...(from ? { from } : {}),
                ...(to ? { to } : {})
            } as Time,
            variables: variables as Variables
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only run on mount
    }, []);

    // Get the partner and client IDs from the tenant context
    const { partnerId, clientId } = useTenant();
    const tenantContext = useMemo<Context>(
        () => ({ variables: { clientId, partnerId } }),
        [clientId, partnerId]
    );

    // We merge all the contexts, in order of precedence:
    // 1. URL context
    // 2. Dashboard context
    // 3. Tenant context
    const initialContext = useMemo<Context>(
        () => merge.all<Context>([dashboardContext, urlContext, tenantContext]),
        [dashboardContext, urlContext, tenantContext]
    );

    // If the time is set, resolve the time props and merge them with the context variables
    if (initialContext.time) {
        initialContext.variables = {
            ...initialContext.variables,
            ...(resolveTimeProps(initialContext.time) as Variables)
        };
    }
    const [context, setContext] = useState(initialContext);

    // Allow updating context (e.g., time/variables)
    const updateContext = (updates: Context) =>
        setContext((prev: Context) => merge(prev, updates));

    const sourceEntries = Object.entries(initialContext.sources || {});
    // Move useEffect and useReactQuery hooks outside of the loop to avoid React Hook violations
    // Create a custom hook to handle the query and effect for each source
    const useSourceQuery = (sourceKey: string, sourceValue: unknown) => {
        const queryResults = useReactQuery(
            { data: sourceValue } as BasePanel,
            initialContext,
            null
        );

        useEffect(() => {
            if (queryResults.data) {
                updateContext({
                    variables: {
                        [sourceKey]: queryResults.data
                    } as Variables
                });
            }
        }, [sourceKey, queryResults.data]);

        return queryResults;
    };

    sourceEntries.forEach(([key, value]) => {
        if (value) {
            // TODO: Fix me. It works as is, but better not violate the rules of
            // eslint-disable-next-line react-hooks/rules-of-hooks
            useSourceQuery(key, value);
        }
    });

    // Update the context if the tenant context changes
    useEffect(() => {
        updateContext(tenantContext);
    }, [tenantContext]);

    // Refresh the context time interval every refreshPeriod
    useInterval(
        () =>
            refreshDashboard &&
            context.time &&
            updateContext({
                variables: {
                    ...resolveTimeProps(context.time)
                } as Variables
            }),
        (refreshDashboard as number) * 1000
    );

    // Update the URL with the current context time.
    // We keep all the other search params, but replace the time params.
    useEffect(() => {
        setSearchParams(
            (prev) => ({
                ...Object.fromEntries(prev),
                ...(context.time?.from
                    ? { from: context.time.from.toString() }
                    : {}),
                ...(context.time?.to ? { to: context.time.to.toString() } : {})
            }),
            // We don't store the search params in the URL, so we need to replace the current URL with the new one.
            { replace: true }
        );
        // We only want to update the URL when the time changes.
    }, [context.time?.from, context.time?.to, setSearchParams]);

    const contextValue = useMemo<DashboardContextValue>(
        () => ({
            context,
            dashboardState: {
                autoAdjust,
                dataUpdatedAt,
                isEditing,
                isFiltrable,
                isSearchable,
                refreshDashboard,
                showAbout
            },
            handleLayoutChange,
            schema: dashboard,
            setAutoAdjust,
            setDataUpdatedAt,
            setRefreshDashboard,
            setSchema,
            setShowAbout,
            updateContext
        }),
        [
            autoAdjust,
            context,
            dashboard,
            dataUpdatedAt,
            handleLayoutChange,
            isEditing,
            isFiltrable,
            isSearchable,
            refreshDashboard,
            setSchema,
            showAbout
        ]
    );
    return (
        <DashboardContext.Provider value={contextValue}>
            {children}
        </DashboardContext.Provider>
    );
};
